import React from 'react';
import { motion } from 'framer-motion';
import { 
  FaShieldAlt, 
  FaLock, 
  FaUserShield, 
  FaDatabase, 
  FaCookie, 
  FaEnvelope,
  FaArrowLeft,
  FaCalendarAlt
} from 'react-icons/fa';
import { Link } from 'react-router-dom';

const PrivacyPolicy = () => {
  const sections = [
    {
      icon: <FaDatabase className="w-6 h-6" />,
      title: "Information We Collect",
      content: "We collect information that you provide directly to us, including your name, email address, phone number, company information, and any other information you choose to provide when contacting us or using our services."
    },
    {
      icon: <FaUserShield className="w-6 h-6" />,
      title: "How We Use Your Information",
      content: "We use the information we collect to provide, maintain, and improve our services, communicate with you, respond to your inquiries, and send you technical notices and updates."
    },
    {
      icon: <FaCookie className="w-6 h-6" />,
      title: "Cookies and Tracking",
      content: "We use cookies and similar tracking technologies to track activity on our website and hold certain information. You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent."
    },
    {
      icon: <FaLock className="w-6 h-6" />,
      title: "Data Security",
      content: "We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction."
    },
    {
      icon: <FaEnvelope className="w-6 h-6" />,
      title: "Contact Us",
      content: "If you have any questions about this Privacy Policy, please contact us at shyeecodes@gmail.com or call +254 762 272 152."
    }
  ];

  const lastUpdated = "January 2026";

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#000324] via-[#010536] to-[#020a5c]">
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_50%,rgba(253,170,1,0.08)_0%,transparent_50%)]" />
        
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <Link 
              to="/"
              className="inline-flex items-center text-white/70 hover:text-[#fdaa01] transition-colors duration-300 mb-8"
            >
              <FaArrowLeft className="mr-2" />
              Back to Home
            </Link>
            
            <div className="w-20 h-20 rounded-3xl bg-[#fdaa01]/10 border border-[#fdaa01]/30 flex items-center justify-center mx-auto mb-6">
              <FaShieldAlt className="text-4xl text-[#fdaa01]" />
            </div>
            
            <h1 className="text-4xl sm:text-5xl font-extrabold text-white mb-4">
              Privacy <span className="text-[#fdaa01]">Policy</span>
            </h1>
            
            <p className="text-white/70 text-lg mb-4">
              Last Updated: {lastUpdated}
            </p>
            
            <p className="text-white/60 max-w-2xl mx-auto leading-relaxed">
              At Shyee Codes, we take your privacy seriously. This policy describes what information we collect and how we use it.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-6">
            {sections.map((section, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl p-8 shadow-xl"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#fdaa01]/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-[#fdaa01]">{section.icon}</span>
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-[#010536] mb-3">
                      {index + 1}. {section.title}
                    </h2>
                    <p className="text-gray-700 leading-relaxed">
                      {section.content}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
          
          {/* CTA Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-12 bg-gradient-to-br from-[#010536] to-[#020a5c] rounded-3xl p-8 sm:p-12 text-center"
          >
            <h2 className="text-2xl font-bold text-white mb-4">
              Questions About Our Privacy Policy?
            </h2>
            <p className="text-white/70 mb-8">
              We're here to help. Contact us anytime.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="mailto:shyeecodes@gmail.com"
                className="inline-flex items-center justify-center px-8 py-4 rounded-xl bg-gradient-to-r from-[#fdaa01] to-[#ffc042] text-[#010536] font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <FaEnvelope className="mr-2" />
                Email Us
              </a>
              <a
                href="https://calendar.app.google/Ti8ismLSofY2gGci8"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-8 py-4 rounded-xl bg-white/10 border border-white/20 text-white font-semibold hover:bg-white/20 transition-all duration-300"
              >
                <FaCalendarAlt className="mr-2" />
                Schedule a Call
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;